import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles"
import { injectIntl } from "gatsby-plugin-intl"
import Layout from "../components/layout"
import MainToolbar from "../components/main-toolbar"
import SEO from "../components/seo"
import RealSelfReviews from "../components/RealSelfReviews"
import {
  testimonials,
  googleSummary,
  googleReviewUrl,
  formatReviewDate,
  translationLabel,
} from "../data/testimonials"

const GOLD = "#C9A24B"
const useStyles = makeStyles({
  page: { background: "#202020", color: "#fff", paddingBottom: 100 },
  hero: {
    background: "#141110",
    padding: "80px 32px 72px",
    "@media (max-width:600px)": { padding: "48px 20px" },
  },
  heroInner: {
    maxWidth: 1216,
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)",
    gap: 48,
    alignItems: "center",
    "@media (max-width:1100px)": { gridTemplateColumns: "1fr", gap: 36 },
  },
  eyebrow: {
    margin: "0 0 20px",
    color: GOLD,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".18em",
    textTransform: "uppercase",
  },
  title: {
    fontSize: "clamp(2.3rem, 4.6vw, 4.2rem)",
    fontWeight: 300,
    lineHeight: 1.08,
    letterSpacing: "-.035em",
    margin: "0 0 24px",
  },
  intro: {
    color: "#cfcccf",
    fontSize: "1.15rem",
    lineHeight: 1.7,
    fontWeight: 300,
    maxWidth: 550,
    margin: 0,
  },
  summaries: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 32,
    "@media (max-width:600px)": { gap: 16 },
  },
  summary: {
    borderLeft: `1px solid ${GOLD}`,
    paddingLeft: 24,
    minWidth: 0,
    "@media (max-width:600px)": { paddingLeft: 16 },
  },
  provider: { fontSize: 20, fontWeight: 500, margin: "0 0 12px" },
  scoreRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 12,
  },
  score: {
    fontSize: "clamp(2.8rem, 6vw, 4.75rem)",
    fontWeight: 300,
    lineHeight: 1.1,
    letterSpacing: "-.04em",
  },
  stars: {
    color: "#645b4c",
    fontSize: 21,
    letterSpacing: ".12em",
    whiteSpace: "nowrap",
    position: "relative",
    display: "inline-block",
    "@media (max-width:600px)": { fontSize: 16 },
  },
  filledStars: {
    color: GOLD,
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  scoreLabel: {
    margin: "14px 0 0",
    fontSize: 16,
    color: "#cfcccf",
    lineHeight: 1.7,
    maxWidth: 320,
  },
  jumpLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: 24,
    marginTop: 30,
    "& a": { color: GOLD, textUnderlineOffset: 6 },
  },
  section: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "72px 32px 0",
    scrollMarginTop: 30,
    "@media (max-width:600px)": { padding: "48px 20px 0" },
  },
  headingRow: {
    display: "flex",
    alignItems: "baseline",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: "clamp(1.8rem, 3vw, 2.5rem)",
    lineHeight: 1.2,
    fontWeight: 300,
    margin: 0,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 24,
    alignItems: "start",
    listStyle: "none",
    margin: 0,
    padding: 0,
    "@media (max-width:760px)": { gridTemplateColumns: "1fr" },
  },
  card: {
    padding: 32,
    border: "1px solid #484341",
    borderRadius: 8,
    background: "#252321",
    position: "relative",
    minWidth: 0,
    "@media (max-width:600px)": { padding: 24 },
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "baseline",
    flexWrap: "wrap",
  },
  author: { fontWeight: 500, fontSize: 17, margin: 0 },
  date: {
    color: "#b8b3b5",
    fontSize: 13,
    lineHeight: 1.6,
    marginLeft: "auto",
  },
  source: {
    color: GOLD,
    fontSize: 13,
    margin: "8px 0 24px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  treatment: {
    fontSize: 19,
    lineHeight: 1.4,
    fontWeight: 400,
    margin: "0 0 16px",
  },
  quote: {
    color: "#e1dedd",
    margin: 0,
    fontWeight: 300,
    fontSize: 17,
    lineHeight: 1.75,
    whiteSpace: "pre-line",
    overflowWrap: "anywhere",
  },
  collapsed: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 6,
    overflow: "hidden",
  },
  cardActions: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 24,
  },
  readMore: {
    font: "inherit",
    color: "#fff",
    background: "none",
    border: 0,
    borderBottom: "1px solid #777",
    padding: "8px 0",
    cursor: "pointer",
    position: "relative",
    zIndex: 1,
  },
  reviewLink: {
    color: GOLD,
    textUnderlineOffset: 5,
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: 8,
    },
    "&:hover::after": { boxShadow: `inset 0 0 0 1px ${GOLD}` },
  },
  translated: { fontSize: 12, color: "#b8b3b5", margin: "18px 0 0" },
  focus: {
    "& a:focus-visible, & button:focus-visible": {
      outline: `2px solid ${GOLD}`,
      outlineOffset: 5,
    },
  },
})

function Testimonial({ review, locale, classes, dateReference }) {
  const [expanded, setExpanded] = useState(false)
  const en = locale === "en"
  const body = review.body[locale]
  const long = body.length > 330
  const google = review.source === "google"
  const googleUrl = googleReviewUrl(review)
  const author = review.author || ""
  const translation = translationLabel(review, locale)

  return (
    <li className={classes.card}>
      <article aria-labelledby={author ? `${review.id}-author` : undefined}>
        <div className={classes.cardTop}>
          {author && (
            <p className={classes.author} id={`${review.id}-author`}>
              {author}
            </p>
          )}
          {review.date && (
            <time className={classes.date} dateTime={review.date}>
              {formatReviewDate(review, locale, dateReference)}
            </time>
          )}
        </div>
        <p className={classes.source}>
          {google ? "Google" : "Face Institute"}
          {google && (
            <span aria-label={en ? "5 out of 5 stars" : "5 van 5 sterren"}>
              ★★★★★
            </span>
          )}
        </p>
        {review.title[locale] && (
          <h3 className={classes.treatment}>{review.title[locale]}</h3>
        )}
        <blockquote
          id={`${review.id}-body`}
          lang={locale}
          className={`${classes.quote} ${
            long && !expanded ? classes.collapsed : ""
          }`}
        >
          {body}
        </blockquote>
        {translation && <p className={classes.translated}>{translation}</p>}
        {(long || googleUrl) && (
          <div className={classes.cardActions}>
            {long && (
              <button
                className={classes.readMore}
                aria-expanded={expanded}
                aria-controls={`${review.id}-body`}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded
                  ? en
                    ? "Read less"
                    : "Lees minder"
                  : en
                  ? "Read more"
                  : "Lees meer"}
              </button>
            )}
            {googleUrl && (
              <a
                href={googleUrl}
                className={classes.reviewLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={
                  author
                    ? `${
                        en ? "Read the review by" : "Lees de review van"
                      } ${author} ${en ? "on Google" : "op Google"}`
                    : en
                    ? "Read the review on Google"
                    : "Lees de review op Google"
                }
              >
                {en ? "Read on Google" : "Lees op Google"}{" "}
                <span aria-hidden="true">↗</span>
              </a>
            )}
          </div>
        )}
      </article>
    </li>
  )
}

Testimonial.propTypes = {
  review: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  dateReference: PropTypes.string.isRequired,
}

const Reviews = ({ intl, location }) => {
  const classes = useStyles()
  const locale = intl.locale === "en" ? "en" : "nl"
  const en = locale === "en"
  const [dateReference, setDateReference] = useState(googleSummary.snapshotDate)
  const summaries = [
    {
      name: "Google",
      rating: googleSummary.rating,
      label: en
        ? `Stars from ${googleSummary.reviewCount} Reviews in Amsterdam and Bergen`
        : `Sterren uit ${googleSummary.reviewCount} reviews in Amsterdam en Bergen`,
    },
    {
      name: "RealSelf",
      rating: 5,
      label: en ? "Stars from 55 Reviews" : "Sterren uit 55 reviews",
    },
  ]

  useEffect(() => {
    const updateDate = () =>
      setDateReference(new Date().toISOString().slice(0, 10))
    updateDate()
    const timer = window.setInterval(updateDate, 60000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <Layout>
      <SEO
        lang={locale}
        title={intl.formatMessage({ id: "reviews.SEOtitle" })}
        description={intl.formatMessage({ id: "reviews.SEO" })}
        path={location.pathname}
      />
      <div className={`${classes.page} ${classes.focus}`}>
        <MainToolbar />
        <main>
          <header className={classes.hero}>
            <div className={classes.heroInner}>
              <div>
                <p className={classes.eyebrow}>Face Institute</p>
                <h1 className={classes.title}>
                  {intl.formatMessage({ id: "reviews.header" })}
                </h1>
                <p className={classes.intro}>
                  {en
                    ? "Our patients share their experiences. From the first consultation to treatment and aftercare."
                    : "Onze patiënten delen hun ervaringen. Van het eerste consult tot de behandeling en nazorg."}
                </p>
                <nav
                  className={classes.jumpLinks}
                  aria-label={en ? "On this page" : "Op deze pagina"}
                >
                  <a href="#testimonials">
                    {en ? "Patient experiences" : "Patiëntervaringen"}
                  </a>
                  <a href="#realself">RealSelf</a>
                </nav>
              </div>
              <div className={classes.summaries}>
                {summaries.map(({ name, rating, label }) => (
                  <div
                    key={name}
                    className={classes.summary}
                    aria-label={en ? `${name} rating` : `${name}-beoordeling`}
                  >
                    <p className={classes.provider}>{name}</p>
                    <div className={classes.scoreRow}>
                      <span className={classes.score}>{rating}</span>
                      <span className={classes.stars} aria-hidden="true">
                        ★★★★★
                        <span
                          className={classes.filledStars}
                          style={{ width: `${(rating / 5) * 100}%` }}
                        >
                          ★★★★★
                        </span>
                      </span>
                    </div>
                    <p className={classes.scoreLabel}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </header>
          <section
            className={classes.section}
            id="realself"
            aria-labelledby="realself-title"
          >
            <div className={classes.headingRow}>
              <h2 className={classes.sectionTitle} id="realself-title">
                RealSelf
              </h2>
            </div>
            <RealSelfReviews locale={locale} />
          </section>
          <section
            className={classes.section}
            id="testimonials"
            aria-labelledby="testimonials-title"
          >
            <div className={classes.headingRow}>
              <h2 className={classes.sectionTitle} id="testimonials-title">
                {en ? "Patient experiences" : "Patiëntervaringen"}
              </h2>
            </div>
            <ol className={classes.grid}>
              {testimonials.map((review) => (
                <Testimonial
                  key={`${locale}-${review.id}`}
                  review={review}
                  locale={locale}
                  classes={classes}
                  dateReference={dateReference}
                />
              ))}
            </ol>
          </section>
        </main>
      </div>
    </Layout>
  )
}

export default injectIntl(Reviews)
